import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Nav, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { aiMonitoringAPI } from '../../services';
import AiVisionBenchDup from './AiVisionBenchDup';
import SampleImage from './VisionBenchSampleImage';

/**
 * Lệnh sếp 18/10/2026 — tab "SO SÁNH MÔ HÌNH" (chỉ ADMIN): quản lý tài khoản + mô hình nhận diện người, chạy kiểm
 * lại trên bộ ảnh mẫu đã lưu, log riêng từng lần chạy, bảng xếp hạng tài khoản/mô hình nào đếm người đúng nhất.
 * - Bộ ảnh mẫu: ảnh gốc sạch cắt quanh vùng của từng lượt quét (máy chủ tự đóng băng mỗi giờ; nút "Cập nhật").
 * - Đáp án: sếp nhập tay ở đây > sếp chấm Telegram > nhân viên xác nhận ("nhận diện sai" = 0).
 * - (19/10/2026) Chế độ "Kiểm trùng ảnh" (AiVisionBenchDup.js); mỗi lần chạy chọn số lượng + "chỉ ảnh chưa kiểm";
 *   Tạm dừng / Tiếp tục khi máy quán nóng hoặc kiểm nhiều quá.
 */
const PROVIDERS = [
  { value: 'CONG_TY', label: 'Tài khoản AI công ty (Claude / OpenAI)' },
  { value: 'YOLO', label: 'YOLO (máy quán, miễn phí)' },
  { value: 'GEMINI', label: 'Google Gemini' },
  { value: 'OPENAI', label: 'OpenAI' },
  { value: 'ANTHROPIC', label: 'Anthropic Claude' },
];
const MODEL_HINT = {
  GEMINI: 'vd gemini-2.5-flash',
  OPENAI: 'vd gpt-4.1-mini',
  ANTHROPIC: 'vd claude-sonnet-5-5',
};
const TRUTH_LABEL = {
  SEP_NHAP: 'Sếp nhập',
  SEP_CHAM: 'Sếp chấm',
  NHAN_VIEN: 'Nhân viên',
  NHAN_DIEN_SAI: 'NV: nhận diện sai',
};
const STATUS = {
  RUNNING: ['warning', 'Đang chạy'],
  PAUSED: ['info', 'Tạm dừng'],
  DONE: ['success', 'Xong'],
  FAILED: ['danger', 'Lỗi'],
  CANCELLED: ['secondary', 'Đã dừng'],
};
const pct = (v) => (v == null ? '—' : `${v}%`);
const num = (v) => (v == null ? '—' : v);
const time = (v) =>
  v ? new Date(v).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '—';

const TruthInput = ({ sample, onSaved }) => {
  const [value, setValue] = useState(sample.truth_count == null ? '' : String(sample.truth_count));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const next = value === '' ? null : Number(value);
    if (next === sample.truth_count || (next != null && (!Number.isInteger(next) || next < 0))) return;
    setBusy(true);
    try {
      onSaved(await aiMonitoringAPI.benchSetTruth(sample.id, next));
    } catch (err) {
      toast.error(err?.message || 'Không lưu được đáp án');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="d-flex align-items-center gap-2">
      <Form.Control
        type="number"
        inputMode="numeric"
        min={0}
        size="sm"
        aria-label="Số người đúng"
        placeholder="Số đúng"
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        style={{ maxWidth: 90, fontSize: 16 }}
      />
      {sample.truth_source && <Badge bg="light" text="dark">{TRUTH_LABEL[sample.truth_source] || sample.truth_source}</Badge>}
    </div>
  );
};

const AccountModal = ({ show, onHide, account, onSaved }) => {
  const [form, setForm] = useState({});
  const [models, setModels] = useState([]);
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!show) return;
    setForm(
      account
        ? { name: account.name, provider: account.provider, model: account.model, account_alias: account.account_alias || '', api_key: '', base_url: account.base_url || '', note: account.note || '' }
        : { name: '', provider: 'CONG_TY', model: '', account_alias: '', api_key: '', base_url: '', note: '' },
    );
    aiMonitoringAPI.benchYoloModels().then((r) => setModels(Array.isArray(r) ? r : [])).catch(() => setModels([]));
    setCompany(null);
    aiMonitoringAPI.benchCompanyAccounts().then((r) => setCompany(Array.isArray(r) ? r : [])).catch(() => setCompany([]));
  }, [show, account]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isYolo = form.provider === 'YOLO';
  const isCompany = form.provider === 'CONG_TY';
  const companyAcc = (company || []).find((a) => a.tai_khoan === form.account_alias);
  // Tên tự điền "tài khoản · mô hình" khi chọn (sếp vẫn sửa được); chỉ ghi đè tên do chính ô này tự điền.
  const pickCompany = (alias, model) =>
    setForm((f) => {
      const auto = !f.name || f.name === `${f.account_alias} · ${f.model}`;
      return { ...f, account_alias: alias, model, name: auto && alias && model ? `${alias} · ${model}` : f.name };
    });
  const save = async () => {
    setBusy(true);
    try {
      const data = { ...form };
      if (account && !data.api_key) delete data.api_key;
      const saved = account
        ? await aiMonitoringAPI.benchUpdateAccount(account.id, data)
        : await aiMonitoringAPI.benchCreateAccount(data);
      toast.success('Đã lưu tài khoản');
      onSaved(saved);
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Không lưu được tài khoản');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{account ? 'Sửa tài khoản / mô hình' : 'Thêm tài khoản / mô hình'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-2">
          <Form.Label>Tên</Form.Label>
          <Form.Control aria-label="Tên tài khoản" value={form.name || ''} onChange={set('name')} placeholder="vd Gemini sếp, YOLO 11x" />
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label>Nhà cung cấp</Form.Label>
          <Form.Select aria-label="Nhà cung cấp" value={form.provider || 'YOLO'} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value, model: '' }))}>
            {PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </Form.Select>
        </Form.Group>
        {isCompany && (
          <Form.Group className="mb-2">
            <Form.Label>Tài khoản công ty</Form.Label>
            {company === null ? (
              <div><Spinner size="sm" animation="border" /></div>
            ) : (
              <Form.Select
                aria-label="Tài khoản công ty"
                value={form.account_alias || ''}
                onChange={(e) => pickCompany(e.target.value, '')}
              >
                <option value="">— chọn tài khoản —</option>
                {company.map((a) => (
                  <option key={a.tai_khoan} value={a.tai_khoan}>
                    {a.tai_khoan} — {a.ten} ({a.loai === 'CLAUDE' ? 'Claude' : 'OpenAI'})
                  </option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
        )}
        <Form.Group className="mb-2">
          <Form.Label>Mô hình</Form.Label>
          {isCompany ? (
            <Form.Select
              aria-label="Mô hình"
              value={form.model || ''}
              disabled={!companyAcc}
              onChange={(e) => pickCompany(form.account_alias, e.target.value)}
            >
              <option value="">{companyAcc ? '— chọn mô hình —' : '— chọn tài khoản trước —'}</option>
              {(companyAcc?.mo_hinh || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Form.Select>
          ) : isYolo ? (
            <Form.Select aria-label="Mô hình" value={form.model || ''} onChange={set('model')}>
              <option value="">— chọn tệp mô hình trên máy quán —</option>
              {models.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Form.Select>
          ) : (
            <Form.Control aria-label="Mô hình" value={form.model || ''} onChange={set('model')} placeholder={MODEL_HINT[form.provider]} />
          )}
          <Form.Text className="text-muted">Đổi mô hình thì kết quả lần chạy sau ghi riêng theo mô hình mới.</Form.Text>
        </Form.Group>
        {!isYolo && !isCompany && (
          <>
            <Form.Group className="mb-2">
              <Form.Label>Khoá API</Form.Label>
              <Form.Control
                type="password"
                autoComplete="off"
                aria-label="Khoá API"
                value={form.api_key || ''}
                onChange={set('api_key')}
                placeholder={account?.has_key ? `${account.api_key_masked} — để trống là giữ khoá cũ` : 'Dán khoá API'}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Địa chỉ API (tuỳ chọn)</Form.Label>
              <Form.Control aria-label="Địa chỉ API" value={form.base_url || ''} onChange={set('base_url')} placeholder="để trống = địa chỉ chuẩn" />
            </Form.Group>
          </>
        )}
        <Form.Group>
          <Form.Label>Ghi chú</Form.Label>
          <Form.Control aria-label="Ghi chú" value={form.note || ''} onChange={set('note')} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Đóng</Button>
        <Button onClick={save} disabled={busy || !form.name || !form.model || (isCompany && !form.account_alias)}>
          {busy ? <Spinner size="sm" animation="border" /> : 'Lưu'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const RunLogModal = ({ run, onHide }) => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [only, setOnly] = useState('');
  useEffect(() => {
    if (!run) return;
    setData(null);
    aiMonitoringAPI
      .benchRunResults(run.id, { page, limit: 50, only: only || undefined })
      .then(setData)
      .catch((err) => toast.error(err?.message || 'Không tải được log'));
  }, [run, page, only]);
  useEffect(() => setPage(1), [run, only]);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  return (
    <Modal show={!!run} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          Log · {run?.account?.name} · {run?.model} · {time(run?.started_at)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Check
          type="switch"
          id="vb-only-error"
          label="Chỉ ảnh lỗi"
          checked={only === 'error'}
          onChange={(e) => setOnly(e.target.checked ? 'error' : '')}
          className="mb-2"
        />
        {!data ? (
          <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
        ) : (
          data.kind === 'DUP' ? (
          <Table size="sm" responsive hover>
            <thead>
              <tr>
                <th>Vùng</th>
                <th>Ảnh A → B</th>
                <th className="text-end">Thuật toán</th>
                <th className="text-end">Mô hình nói</th>
                <th className="text-end">Đáp án</th>
                <th className="text-end">ms</th>
                <th>Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const label = (v) => (v == null ? '—' : v ? 'Trùng' : 'Khác');
                const hit = r.truth_dup != null && r.predicted_dup != null ? r.truth_dup === r.predicted_dup : null;
                return (
                  <tr key={r.id}>
                    <td>{r.frame_label || '—'}</td>
                    <td className="small">{time(r.detected_at_a)} → {time(r.detected_at)}</td>
                    <td className="text-end small">{label(r.algo_dup)} ({num(r.algo_diff)})</td>
                    <td className={`text-end fw-semibold ${hit === false ? 'text-danger' : hit ? 'text-success' : ''}`}>{label(r.predicted_dup)}</td>
                    <td className="text-end">{label(r.truth_dup)}</td>
                    <td className="text-end">{num(r.latency_ms)}</td>
                    <td className="small text-muted" style={{ maxWidth: 260 }}>{r.error || r.answer || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          ) : (
          <Table size="sm" responsive hover>
            <thead>
              <tr>
                <th>Vùng</th>
                <th>Lúc quét</th>
                <th className="text-end">Máy quét gốc</th>
                <th className="text-end">Mô hình đếm</th>
                <th className="text-end">Đáp án</th>
                <th className="text-end">Lệch</th>
                <th className="text-end">ms</th>
                <th>Ghi chú</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td>{r.frame_label || r.external_region_id || '—'}</td>
                  <td>{time(r.detected_at)}</td>
                  <td className="text-end">{num(r.scan_count)}</td>
                  <td className="text-end fw-semibold">{num(r.predicted_count)}</td>
                  <td className="text-end">{num(r.truth_count)}</td>
                  <td className={`text-end ${r.diff ? 'text-danger' : r.diff === 0 ? 'text-success' : ''}`}>
                    {r.diff == null ? '—' : r.diff > 0 ? `+${r.diff}` : r.diff}
                  </td>
                  <td className="text-end">{num(r.latency_ms)}</td>
                  <td className="small text-muted" style={{ maxWidth: 260 }}>{r.error || r.answer || ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          )
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="small text-muted">{data ? `${data.total} ${data.kind === 'DUP' ? 'cặp' : 'ảnh'}` : ''}</span>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Trước</Button>
          <span className="small align-self-center">{page}/{pages}</span>
          <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Sau</Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const AiVisionBenchTab = ({ branchId }) => {
  const [accounts, setAccounts] = useState([]);
  const [board, setBoard] = useState(null);
  const [runs, setRuns] = useState([]);
  const [samples, setSamples] = useState(null);
  const [filter, setFilter] = useState('no');
  const [page, setPage] = useState(1);
  // Mặc định chạy trên tất cả ảnh: bộ mẫu mới thường chưa có đáp án (chạy "có đáp án" sẽ rỗng).
  const [scope, setScope] = useState('ALL');
  const [mode, setMode] = useState('COUNT');
  const [limit, setLimit] = useState('100');
  const [pendingOnly, setPendingOnly] = useState(true);
  const [dupKey, setDupKey] = useState(0);
  const [editing, setEditing] = useState(undefined);
  const [logRun, setLogRun] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadAccounts = useCallback(
    () => aiMonitoringAPI.benchAccounts().then(setAccounts).catch((err) => toast.error(err?.message || 'Không tải được tài khoản')),
    [],
  );
  const loadBoard = useCallback(() => aiMonitoringAPI.benchLeaderboard(branchId).then(setBoard).catch(() => setBoard(null)), [branchId]);
  const loadRuns = useCallback(() => aiMonitoringAPI.benchRuns().then(setRuns).catch(() => {}), []);
  const loadSamples = useCallback(
    () =>
      aiMonitoringAPI
        .benchSamples({ branch_id: branchId, labeled: filter || undefined, page, limit: 24 })
        .then(setSamples)
        .catch((err) => toast.error(err?.message || 'Không tải được ảnh mẫu')),
    [branchId, filter, page],
  );

  useEffect(() => {
    loadAccounts();
    loadRuns();
  }, [loadAccounts, loadRuns]);
  useEffect(() => {
    loadBoard();
  }, [loadBoard]);
  useEffect(() => {
    loadSamples();
  }, [loadSamples]);
  useEffect(() => setPage(1), [filter, branchId]);

  // Có lần chạy dở ⇒ hỏi tiến độ mỗi 3 giây; xong thì nạp lại bảng xếp hạng.
  const running = runs.some((r) => r.status === 'RUNNING');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => {
      aiMonitoringAPI.benchRuns().then((next) => {
        setRuns(next);
        if (!next.some((r) => r.status === 'RUNNING')) {
          loadBoard();
          loadSamples();
          setDupKey((k) => k + 1);
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [running, loadBoard, loadSamples]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await aiMonitoringAPI.benchSyncSamples(branchId);
      if (r?.no_cam_user) toast.warn('Tài khoản của sếp chưa được cấp quyền xem camera — không lấy được ảnh');
      else {
        toast.success(
          `Thêm ${r?.created ?? 0} ảnh mẫu, bỏ ${r?.duplicates ?? 0} ảnh trùng, gắn ${r?.labeled ?? 0} đáp án, ghép ${r?.pairs ?? 0} cặp kiểm trùng`,
        );
      }
      loadSamples();
      loadBoard();
      setDupKey((k) => k + 1);
    } catch (err) {
      toast.error(err?.message || 'Không cập nhật được bộ mẫu');
    } finally {
      setSyncing(false);
    }
  };

  const start = async (acc) => {
    const n = limit.trim() === '' ? null : Number(limit);
    if (n != null && (!Number.isInteger(n) || n < 1)) {
      toast.error('Số lượng phải là số nguyên dương (để trống = chạy hết)');
      return;
    }
    const what = mode === 'DUP' ? 'cặp ảnh' : 'ảnh mẫu';
    const how = `${n ? `tối đa ${n}` : 'tất cả'} ${what}${pendingOnly ? ' chưa kiểm' : ''}`;
    const note =
      acc.provider === 'CONG_TY'
        ? `Chạy "${acc.name}" trên ${how} sẽ dùng hạn mức tài khoản ${acc.account_alias} (~10–60 giây mỗi ${mode === 'DUP' ? 'cặp' : 'ảnh'}). Chạy?`
        : `Chạy "${acc.name}" trên ${how} sẽ gọi API trả phí cho từng ${mode === 'DUP' ? 'cặp' : 'ảnh'}. Chạy?`;
    if (acc.provider !== 'YOLO' && !window.confirm(note)) return;
    try {
      await aiMonitoringAPI.benchStartRun({
        account_id: acc.id,
        kind: mode,
        scope,
        branch_id: branchId,
        limit: n,
        pending_only: pendingOnly,
      });
      toast.success('Đã bắt đầu chạy kiểm lại');
      loadRuns();
    } catch (err) {
      toast.error(err?.message || 'Không chạy được');
    }
  };

  const control = async (fn, r, ok) => {
    try {
      await fn(r.id);
      toast.success(ok);
    } catch (err) {
      toast.error(err?.message || 'Không thực hiện được');
    }
    loadRuns();
  };

  const remove = async (acc) => {
    if (!window.confirm(`Xoá tài khoản "${acc.name}" và toàn bộ log của nó?`)) return;
    try {
      await aiMonitoringAPI.benchDeleteAccount(acc.id);
      loadAccounts();
      loadBoard();
      loadRuns();
    } catch (err) {
      toast.error(err?.message || 'Không xoá được');
    }
  };

  const rows = board ? [...board.items, ...(board.baseline?.images ? [board.baseline] : [])] : [];
  const pages = samples ? Math.max(1, Math.ceil(samples.total / samples.limit)) : 1;

  return (
    <div className="vision-bench" data-testid="vision-bench">
      <Nav variant="pills" activeKey={mode} onSelect={(k) => setMode(k)} className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="COUNT">Đếm người</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="DUP">Kiểm trùng ảnh</Nav.Link>
        </Nav.Item>
      </Nav>

      {mode === 'COUNT' && (
      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-2">Bảng xếp hạng nhận diện người</h6>
          <p className="small text-muted mb-2">
            Mỗi tài khoản + mô hình lấy kết quả mới nhất của từng ảnh, chấm theo đáp án hiện tại. Chỉ ảnh có đáp án
            mới được tính điểm.
          </p>
          {rows.length === 0 ? (
            <div className="small text-muted">Chưa có kết quả — thêm tài khoản rồi bấm "Chạy kiểm lại".</div>
          ) : (
            <Table size="sm" responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tài khoản</th>
                  <th>Mô hình</th>
                  <th className="text-end">Ảnh chấm</th>
                  <th className="text-end">Đúng</th>
                  <th className="text-end">Lệch ≤1</th>
                  <th className="text-end">Sai số TB</th>
                  <th className="text-end">Thiên lệch</th>
                  <th className="text-end">Lỗi</th>
                  <th className="text-end">ms/ảnh</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.account_id}|${r.model}`} className={r.account_id ? '' : 'text-muted fst-italic'}>
                    <td>{r.account_id ? i + 1 : ''}</td>
                    <td>{r.account_name}</td>
                    <td className="small">{r.model}</td>
                    <td className="text-end">{r.scored}</td>
                    <td className="text-end fw-semibold">{pct(r.exact_pct)}</td>
                    <td className="text-end">{pct(r.within1_pct)}</td>
                    <td className="text-end">{num(r.mae)}</td>
                    <td className="text-end">{r.bias == null ? '—' : r.bias > 0 ? `+${r.bias}` : r.bias}</td>
                    <td className="text-end">{r.errors}</td>
                    <td className="text-end">{num(r.avg_latency_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      )}

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h6 className="mb-0">Tài khoản &amp; mô hình</h6>
            <Button size="sm" onClick={() => setEditing(null)}>+ Thêm tài khoản</Button>
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-center mb-2 small">
            <span className="text-muted">Mỗi lần chạy:</span>
            <Form.Select size="sm" aria-label="Phạm vi chạy" value={scope} onChange={(e) => setScope(e.target.value)} style={{ width: 'auto' }}>
              <option value="LABELED">{mode === 'DUP' ? 'Cặp có đáp án' : 'Ảnh có đáp án'}</option>
              <option value="ALL">{mode === 'DUP' ? 'Tất cả cặp' : 'Tất cả ảnh'}</option>
            </Form.Select>
            <Form.Control
              size="sm"
              type="number"
              inputMode="numeric"
              min={1}
              aria-label="Số lượng mỗi lần chạy"
              placeholder="Tất cả"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              style={{ width: 90, fontSize: 16 }}
            />
            <span>{mode === 'DUP' ? 'cặp' : 'ảnh'}</span>
            <Form.Check
              type="switch"
              id="vb-pending-only"
              label={mode === 'DUP' ? 'Chỉ cặp chưa kiểm' : 'Chỉ ảnh chưa kiểm'}
              checked={pendingOnly}
              onChange={(e) => setPendingOnly(e.target.checked)}
            />
          </div>
          <p className="small text-muted mb-2">
            Để trống số lượng = chạy hết. Bật "chưa kiểm" thì mỗi lần bấm chạy đợt kế tiếp (ảnh mà tài khoản + mô hình đó
            chưa có kết quả).
          </p>
          {accounts.length === 0 ? (
            <div className="small text-muted">Chưa có tài khoản nào.</div>
          ) : (
            <Table size="sm" responsive className="mb-0">
              <tbody>
                {accounts.map((a) => {
                  const live = runs.find((r) => r.account_id === a.id && (r.status === 'RUNNING' || r.status === 'PAUSED'));
                  const noDup = mode === 'DUP' && a.provider === 'YOLO';
                  return (
                    <tr key={a.id}>
                      <td className="fw-semibold">{a.name}</td>
                      <td className="small">
                        {a.provider === 'CONG_TY' ? `Công ty · ${a.account_alias}` : PROVIDERS.find((p) => p.value === a.provider)?.label || a.provider}
                      </td>
                      <td className="small">{a.model}</td>
                      <td className="small text-muted">{a.provider === 'YOLO' ? '' : a.api_key_masked}</td>
                      <td className="text-end text-nowrap">
                        <Button
                          size="sm"
                          variant="primary"
                          className="me-1"
                          disabled={!!live || noDup}
                          title={noDup ? 'YOLO chỉ đếm người, không so được 2 ảnh' : undefined}
                          onClick={() => start(a)}
                        >
                          {live
                            ? `${live.status === 'PAUSED' ? 'Tạm dừng' : 'Đang chạy'} ${live.done}/${live.total}`
                            : mode === 'DUP'
                              ? 'Chạy kiểm trùng'
                              : 'Chạy đếm người'}
                        </Button>
                        <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setEditing(a)}>Sửa</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => remove(a)}>Xoá</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <h6 className="mb-2">Các lần chạy (log)</h6>
          {runs.length === 0 ? (
            <div className="small text-muted">Chưa chạy lần nào.</div>
          ) : (
            <Table size="sm" responsive hover className="mb-0">
              <tbody>
                {runs.slice(0, 15).map((r) => {
                  const [bg, label] = STATUS[r.status] || ['secondary', r.status];
                  return (
                    <tr key={r.id}>
                      <td className="small">{time(r.started_at)}</td>
                      <td>{r.account?.name}</td>
                      <td className="small">{r.model}</td>
                      <td className="small">{r.kind === 'DUP' ? 'Kiểm trùng' : 'Đếm người'}</td>
                      <td><Badge bg={bg}>{label}</Badge></td>
                      <td style={{ minWidth: 140 }}>
                        <ProgressBar now={r.total ? (r.done / r.total) * 100 : 0} label={`${r.done}/${r.total}`} style={{ height: 16 }} />
                      </td>
                      <td className="small text-muted">{r.errors ? `${r.errors} lỗi` : ''}</td>
                      <td className="text-end text-nowrap">
                        {r.status === 'RUNNING' && (
                          <Button size="sm" variant="outline-warning" className="me-1" onClick={() => control(aiMonitoringAPI.benchPauseRun, r, 'Đã tạm dừng — ảnh đang chạy dở sẽ xong rồi dừng')}>
                            Tạm dừng
                          </Button>
                        )}
                        {r.status === 'PAUSED' && (
                          <Button size="sm" variant="outline-success" className="me-1" onClick={() => control(aiMonitoringAPI.benchResumeRun, r, 'Đã tiếp tục chạy')}>
                            Tiếp tục
                          </Button>
                        )}
                        {(r.status === 'RUNNING' || r.status === 'PAUSED') && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="me-1"
                            onClick={() => window.confirm('Dừng hẳn lần chạy này? (không tiếp tục được nữa)') && control(aiMonitoringAPI.benchCancelRun, r, 'Đã dừng hẳn')}
                          >
                            Dừng hẳn
                          </Button>
                        )}
                        <Button size="sm" variant="outline-secondary" onClick={() => setLogRun(r)}>Xem log</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {mode === 'DUP' && <AiVisionBenchDup branchId={branchId} refreshKey={dupKey} />}

      {mode === 'COUNT' && (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <h6 className="mb-0">
              Bộ ảnh mẫu{samples ? ` · ${samples.totals.labeled}/${samples.totals.all} ảnh có đáp án` : ''}
            </h6>
            <div className="d-flex gap-2 align-items-center">
              <Form.Select size="sm" aria-label="Lọc ảnh mẫu" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: 'auto' }}>
                <option value="no">Chưa có đáp án</option>
                <option value="yes">Đã có đáp án</option>
                <option value="">Tất cả</option>
              </Form.Select>
              <Button size="sm" variant="outline-primary" onClick={sync} disabled={syncing}>
                {syncing ? <Spinner size="sm" animation="border" /> : 'Cập nhật bộ mẫu'}
              </Button>
            </div>
          </div>
          <p className="small text-muted">
            Nhập số người đúng trong viền đỏ rồi bấm Enter. Viền đỏ là vùng sếp đã khoanh (Khoanh vùng camera); ảnh không có vùng khoanh trùng tên thì không đưa vào bộ mẫu.
          </p>
          {!samples ? (
            <div className="text-center py-3"><Spinner size="sm" animation="border" /></div>
          ) : samples.items.length === 0 ? (
            <div className="small text-muted">Không có ảnh — bấm "Cập nhật bộ mẫu".</div>
          ) : (
            <Row xs={1} sm={2} lg={3} xl={4} className="g-3">
              {samples.items.map((s) => (
                <Col key={s.id}>
                  <Card className="cambill-card h-100">
                    <div className="cambill-img-frame">
                      <SampleImage id={s.id} box={s.inner_box} points={s.region_points} />
                    </div>
                    <Card.Body>
                      <div className="cambill-card-head">
                        <span className="cambill-card-title">{s.frame_label || s.external_region_id}</span>
                        <span className="cambill-card-time">{time(s.detected_at)}</span>
                      </div>
                      <div className="small mb-2">Máy quét gốc đếm: <b>{s.scan_count}</b></div>
                      <TruthInput
                        sample={s}
                        onSaved={(u) => {
                          setSamples((cur) => ({ ...cur, items: cur.items.map((x) => (x.id === u.id ? { ...x, ...u } : x)) }));
                          loadBoard();
                        }}
                      />
                      {s.predictions?.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-2">
                          {s.predictions.map((p) => (
                            <Badge
                              key={`${p.account_id}|${p.model}`}
                              bg={p.predicted_count == null ? 'secondary' : s.truth_count == null ? 'info' : p.predicted_count === s.truth_count ? 'success' : 'danger'}
                              title={p.error || p.model}
                            >
                              {p.account_name}: {p.predicted_count ?? 'lỗi'}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          {samples && pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-3">
              <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Trước</Button>
              <span className="small align-self-center">{page}/{pages}</span>
              <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Sau</Button>
            </div>
          )}
        </Card.Body>
      </Card>
      )}

      <AccountModal
        show={editing !== undefined}
        account={editing || null}
        onHide={() => setEditing(undefined)}
        onSaved={() => {
          loadAccounts();
          loadBoard();
        }}
      />
      <RunLogModal run={logRun} onHide={() => setLogRun(null)} />
    </div>
  );
};

export default AiVisionBenchTab;
